import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { listHands } from '../api/hands';
import { getMyVotes } from '../api/votes';
import { listFavorites } from '../api/favorites';
import { createPortalSession } from '../api/subscriptions';
import { API_ORIGIN } from '../api/client';
import { getTileLabel } from '../utils/tileHelpers';
import { CertifiedBadge } from '../components/auth/LoginButton';
import Spinner from '../components/common/Spinner';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import TileImage from '../components/tiles/TileImage';

type Tab = 'posts' | 'votes' | 'favorites';

export default function ProfilePage() {
  const { user, isLoading: authLoading, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('posts');
  const [portalLoading, setPortalLoading] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const { portal_url } = await createPortalSession();
      window.location.href = portal_url;
    } catch (err: any) {
      alert(err.message ?? 'ポータルを開けませんでした');
    } finally {
      setPortalLoading(false);
    }
  };

  const { data: myHands, isLoading: handsLoading } = useQuery({
    queryKey: ['hands', 'mine'],
    queryFn: () => listHands({ mine: true, page_size: 20 }),
    enabled: !!user && activeTab === 'posts',
  });

  const { data: myVotes, isLoading: votesLoading } = useQuery({
    queryKey: ['votes', 'mine'],
    queryFn: () => getMyVotes({ page_size: 20 }),
    enabled: !!user && activeTab === 'votes',
  });

  const { data: myFavorites, isLoading: favoritesLoading } = useQuery({
    queryKey: ['favorites'],
    queryFn: () => listFavorites({ page_size: 20 }),
    enabled: !!user && activeTab === 'favorites',
  });

  if (authLoading) {
    return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;
  }

  if (!user) {
    return (
      <div className="text-center py-20 space-y-3">
        <p className="text-gray-600">プロフィールを表示するにはログインが必要です</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 space-y-6">
      {/* Profile header */}
      <div className="flex items-center gap-4 p-4 border rounded-xl bg-white">
        {user.x_avatar_url ? (
          <img src={user.x_avatar_url} alt="" className="w-16 h-16 rounded-full" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-2xl font-bold">
            {user.x_display_name[0]}
          </div>
        )}
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold">{user.x_display_name}</span>
            {user.is_pro && <CertifiedBadge />}
          </div>
          <span className="text-gray-500">@{user.x_username}</span>
          <div className="mt-1">
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium
              ${user.subscription_tier === 'pro'
                ? 'bg-yellow-100 text-yellow-800'
                : user.subscription_tier === 'standard'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-600'}`}
            >
              {user.subscription_tier === 'pro'
                ? 'プロプラン'
                : user.subscription_tier === 'standard'
                ? 'プレミアム'
                : '無料プラン'}
            </span>
          </div>
        </div>
        <div className="ml-auto flex flex-col items-end gap-2">
          <Link to="/pricing">
            <Button variant="outline" size="sm">プランを変更</Button>
          </Link>
          {user.subscription_tier !== 'free' && (
            <button
              onClick={handlePortal}
              disabled={portalLoading}
              className="text-xs text-gray-400 hover:text-red-500 disabled:opacity-50 transition-colors"
            >
              {portalLoading ? '読み込み中...' : 'サブスクを解約'}
            </button>
          )}
          {!user.is_pro && (
            <Link to="/apply-pro" className="text-xs text-blue-500 hover:underline">
              公認雀士に申請
            </Link>
          )}
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="text-xs text-gray-400 hover:text-red-500 transition-colors"
          >
            ログアウト
          </button>
        </div>
      </div>

      <Modal isOpen={showLogoutConfirm} onClose={() => setShowLogoutConfirm(false)} title="ログアウト">
        <div className="space-y-4 text-sm text-gray-700">
          <p>ログアウトしますか？</p>
          <div className="flex gap-3 justify-end pt-1">
            <button
              onClick={() => setShowLogoutConfirm(false)}
              className="px-4 py-2 text-gray-500 hover:text-gray-700"
            >
              キャンセル
            </button>
            <button
              onClick={() => { setShowLogoutConfirm(false); logout(); }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
            >
              ログアウト
            </button>
          </div>
        </div>
      </Modal>

      {/* Tabs */}
      <div className="flex border-b">
        <button
          onClick={() => setActiveTab('posts')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'posts'
              ? 'border-green-600 text-green-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          投稿した局面
        </button>
        <button
          onClick={() => setActiveTab('votes')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'votes'
              ? 'border-green-600 text-green-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          投票履歴
        </button>
        <button
          onClick={() => setActiveTab('favorites')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'favorites'
              ? 'border-green-600 text-green-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          ★ お気に入り
        </button>
      </div>

      {/* Tab content */}
      {activeTab === 'posts' && (
        <div>
          {handsLoading ? (
            <Spinner />
          ) : !myHands || myHands.length === 0 ? (
            <div className="text-center text-gray-500 py-8 border rounded-xl">
              まだ局面を投稿していません
              <div className="mt-3">
                <Link to="/">
                  <Button size="sm">最初の局面を投稿する</Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {myHands.map((hand) => {
                const imageUrl = hand.source_image_url
                  ? hand.source_image_url.startsWith('/')
                    ? `${API_ORIGIN}${hand.source_image_url}`
                    : hand.source_image_url
                  : null;
                return (
                  <Link
                    key={hand.id}
                    to={`/hands/${hand.id}`}
                    className="flex items-center gap-3 p-3 border rounded-xl hover:bg-gray-50"
                  >
                    {imageUrl && (
                      <img
                        src={imageUrl}
                        alt=""
                        className="w-14 h-10 object-cover rounded border shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{hand.title ?? '無題の局面'}</div>
                      <div className="text-xs text-gray-400">
                        {new Date(hand.created_at).toLocaleDateString('ja-JP')}
                        {' · '}💬 {hand.vote_count}票
                        {' · '}{hand.is_public ? '公開' : '非公開'}
                      </div>
                    </div>
                    {hand.mortal_best_tile && <TileImage tile={hand.mortal_best_tile} size="sm" />}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'favorites' && (
        <div>
          {favoritesLoading ? (
            <Spinner />
          ) : !myFavorites || myFavorites.length === 0 ? (
            <div className="text-center text-gray-500 py-8 border rounded-xl">
              まだお気に入りがありません
              <div className="mt-3">
                <Link to="/hands">
                  <Button size="sm">局面を探す</Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {myFavorites.map((hand) => {
                const imageUrl = hand.source_image_url
                  ? hand.source_image_url.startsWith('/')
                    ? `${API_ORIGIN}${hand.source_image_url}`
                    : hand.source_image_url
                  : null;
                return (
                  <Link
                    key={hand.id}
                    to={`/hands/${hand.id}`}
                    className="flex items-center gap-3 p-3 border rounded-xl hover:bg-gray-50"
                  >
                    {imageUrl && (
                      <img src={imageUrl} alt="" className="w-14 h-10 object-cover rounded border shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{hand.title ?? '無題の局面'}</div>
                      <div className="text-xs text-gray-400">
                        {new Date(hand.created_at).toLocaleDateString('ja-JP')}
                        {' · '}💬 {hand.vote_count}票
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'votes' && (
        <div>
          {votesLoading ? (
            <Spinner />
          ) : !myVotes || myVotes.length === 0 ? (
            <div className="text-center text-gray-500 py-8 border rounded-xl">
              まだ投票していません
              <div className="mt-3">
                <Link to="/hands">
                  <Button size="sm">局面を探す</Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {myVotes.map((vote) => {
                const imageUrl = vote.hand.source_image_url
                  ? vote.hand.source_image_url.startsWith('/')
                    ? `${API_ORIGIN}${vote.hand.source_image_url}`
                    : vote.hand.source_image_url
                  : null;
                return (
                  <Link
                    key={vote.id}
                    to={`/hands/${vote.hand.id}`}
                    className="flex items-center gap-3 p-3 border rounded-xl hover:bg-gray-50"
                  >
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt=""
                        className="w-14 h-10 object-cover rounded border shrink-0"
                      />
                    ) : (
                      <div className="w-14 h-10 bg-gray-100 rounded border shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate text-sm">
                        {vote.hand.title ?? '無題の局面'}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {new Date(vote.created_at).toLocaleDateString('ja-JP')}
                      </div>
                      {vote.comment && (
                        <div className="text-xs text-gray-500 truncate mt-0.5">
                          "{vote.comment}"
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 flex flex-col items-center gap-0.5">
                      {vote.chosen_tile ? (
                        <>
                          <TileImage tile={vote.chosen_tile} size="sm" />
                          <span className="text-xs text-green-600 font-medium">
                            {getTileLabel(vote.chosen_tile)}
                          </span>
                        </>
                      ) : (
                        <span className="text-xs text-gray-500 font-medium">鳴かない</span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
